// Admin-operations audit-log action-code catalog. Append-only: existing
// codes must never be removed or renamed because historical audit rows
// reference these literals forever.
//
// The codes form an append-only catalog: 10 from (d) #91 plus 5 added in
// (c) #93 (`role.*` + `role.permissions.set`), plus `user.self-update` added
// for self-service profile editing, plus 3 carrier codes for the master-
// catalog CRUD module, plus 7 carrier child/lifecycle codes, plus 9 request
// codes for the request domain capsule
// (#314 — every mutating endpoint emits one AuditEvent next to one
// RequestAuditEntry inside the same Prisma `$transaction`), plus
// `user.shift-toggle` for the self-shift toggle (#315), plus 2 manual
// chat unread codes (#570 — `mark-unread` / `unmark-unread` are
// explicit user actions; automatic visibility-driven reads are NOT
// audited), plus 14 `client.*` codes for the client aggregate capsule
// (#601 — every mutating endpoint emits one AuditEvent next to the
// ClientVersion snapshot inside the same Prisma `$transaction`; payloads
// are non-PII metadata only, the full PII surface lives in
// `ClientVersion.snapshot`). Existing rows must keep their action label
// valid forever — codes never get removed or renamed.

/**
 * @aiInvariant cardinality is exactly 96 (#1261 appended 2 CRM
 *   lead-intake admin codes: `leadIntakeDuty.toggle` +
 *   `callCenterAgent.bind`); future sub-issues append codes only —
 *   never remove or rename, since historical audit rows reference these literals
 *   forever. The cardinality lock-test is an anti-drift tripwire.
 */
export const ACTION_CODES = [
  'user.create',
  'user.update',
  'user.deactivate',
  'user.reactivate',
  'user.resetPassword',
  'user.changePassword',
  'user.self-update',
  'user.shift-toggle',
  'department.create',
  'department.update',
  'department.archive',
  'department.restore',
  'role.create',
  'role.update',
  'role.archive',
  'role.restore',
  'role.permissions.set',
  'carrier.create',
  'carrier.update',
  'carrier.deactivate',
  'carrier.activate',
  'carrier.warehouse.create',
  'carrier.warehouse.update',
  'carrier.warehouse.delete',
  'carrier.priceList.create',
  'carrier.priceList.update',
  'carrier.priceList.delete',
  'request.create',
  'request.update',
  'request.delete',
  'request.transition',
  'request.winner',
  'request.carrier.add',
  'request.carrier.update',
  'request.carrier.remove',
  'request.chat',
  // INVARIANT (#570): manual mark/unmark of a request chat as unread.
  // Distinct from `request.chat` (chat-post audit) so the operator can
  // separate "wrote a message" from "personal-state flag flipped".
  // Visibility-driven auto-read is NOT audited (Resolved Decisions §
  // Audit).
  'request.chat.markUnread',
  'request.chat.unmarkUnread',
  // INVARIANT: emitted exactly once per `POST /requests/draft` whose
  // `clientToken` collides with an already-committed row owned by the
  // SAME manager (#415 slice 2). Distinguishes the dedup catch path
  // from `request.create` so the operator can see retry-rate signal
  // separately from genuine create traffic. Never emitted for
  // cross-manager collisions — those throw 409
  // `RequestDraftClientTokenConflictError` and are not audited as
  // resolves (the audit row would imply we returned a row to the
  // caller, which we did not).
  'request.draft.idempotent_resolve',
  // INVARIANT (#707): emitted on the parent China `Request` when a
  // `RussiaRequest` snapshot is minted via `createFromParent`. Paired
  // with a `request.childRussiaCreated` per-request ledger row on the
  // parent (`RequestAuditAction.CHILD_RUSSIA_CREATED`) inside the same
  // Prisma `$transaction` as the russia row + russia ledger writes.
  'request.childRussiaCreated',
  // INVARIANT (#1178): one admin-ops audit row per successful regroup
  //   PATCH on the narrow `/requests/:id/boxes/:boxId/items/group-ordinals`
  //   endpoint. Paired with one `RequestAuditAction.BOX_REGROUP` ledger
  //   row inside the same Prisma `$transaction`. Failed PATCH writes
  //   neither; no-op batch writes neither (D16). Payload carries non-PII
  //   metadata `{ boxId, changedCount }` only — per-item from/to deltas
  //   live on the per-request ledger row.
  'request.box.regroup',
  // INVARIANT (#601): client aggregate capsule. Every mutating endpoint
  // emits exactly one of these codes inside the same Prisma `$transaction`
  // that writes the business row + `ClientVersion` snapshot. Payloads
  // never contain raw phone, INN, comments, requisites, contact names, or
  // legal entity names — those live exclusively in
  // `ClientVersion.snapshot`. `client.version.restore` is the action emitted
  // by `POST /api/clients/:id/versions/:versionId/restore`.
  'client.create',
  'client.update',
  'client.archive',
  'client.restore',
  'client.version.restore',
  'client.contact.add',
  'client.contact.update',
  'client.contact.archive',
  'client.contact.restore',
  'client.contact.setPrimary',
  'client.legal.add',
  'client.legal.update',
  'client.legal.archive',
  'client.legal.restore',
  // #2242: bank-requisites sets (1..n per legal entity). Emitted inside the
  // same `$transaction` as the requisites write; diff payloads carry the
  // requisites row id + changed field-name list only — never `details` /
  // `account` / `bik` values (those live in `ClientVersion.snapshot`).
  'client.legal.requisites.add',
  'client.legal.requisites.update',
  'client.legal.requisites.archive',
  'client.legal.requisites.restore',
  // #2246 (D-018): the CLOSING→DEAL conversion flips the client's derived
  // `lifecycle` to WORKING on its first deal THROUGH the aggregate contract
  // (superseding the #1830 raw-SQL bypass). This code audits that flip; its diff
  // carries the lifecycle transition + client id only — never PII/secrets.
  'client.lifecycle.working',
  // INVARIANT (#623): reference-data directory mutations. Each code is
  //   emitted inside the same Prisma `$transaction` that writes the
  //   business row. Payloads carry diffed field deltas only — never
  //   actor tokens/secrets. City names are not treated as sensitive.
  //   `archive` / `restore` correspond to the `DELETE` / `activate`
  //   lifecycle endpoints (soft-delete pattern); `update` covers PATCH
  //   field updates against an active row.
  'transportType.create',
  'transportType.update',
  'transportType.archive',
  'transportType.restore',
  'arrivalCity.create',
  'arrivalCity.update',
  'arrivalCity.archive',
  'arrivalCity.restore',
  // INVARIANT (#1166): RU-internal carriers directory lifecycle. PII
  //   field `contacts` is included in the diff payload — the audit
  //   surface is privileged (`audit.read`) and we need restorable
  //   change history for the directory.
  'russiaCarrier.create',
  'russiaCarrier.update',
  'russiaCarrier.archive',
  'russiaCarrier.restore',
  // INVARIANT (#995): request-level reference directories. Each code is
  //   emitted inside the same Prisma `$transaction` that writes the
  //   business row. `archive` / `restore` correspond to the `DELETE` /
  //   `activate` lifecycle endpoints; `update` covers PATCH field
  //   updates against an active row.
  'roadOption.create',
  'roadOption.update',
  'roadOption.archive',
  'roadOption.restore',
  'packagingOption.create',
  'packagingOption.update',
  'packagingOption.archive',
  'packagingOption.restore',
  // INVARIANT (#705): Russia-request capsule. Slice 1 ships CRUD; Slice 2b
  // adds `russiaRequest.transition` — one code for every transition, with
  // the specific transition key (send/return/process/close) carried in the
  // `diff.transition` field (mirrors how the China side `request.transition`
  // single code multiplexes all transition keys). Emitted inside the same
  // Prisma `$transaction` that writes the `RussiaRequestAuditEntry` ledger.
  'russiaRequest.create',
  'russiaRequest.update',
  'russiaRequest.delete',
  'russiaRequest.transition',
  // INVARIANT (#1066): Russia-request chat codes. Mirror of
  //   `request.chat*` on the China side. `russiaRequest.chat` covers
  //   the post path; the two `*.markUnread` / `*.unmarkUnread` codes
  //   audit the explicit user actions on the read-state row.
  'russiaRequest.chat',
  'russiaRequest.chat.markUnread',
  'russiaRequest.chat.unmarkUnread',
  // INVARIANT (#1227): Cargo ("Грузы") admin-ops audit codes for the
  //   NextKargo registry port. `AuditEvent.action` is a plain `String`
  //   column (see `audit.prisma`), so these are shared-only catalog
  //   additions — NO Prisma enum and NO migration. Each mutating Cargo
  //   endpoint (#1228/#1230/#1231/#1232) emits exactly one of these inside
  //   the same Prisma `$transaction` as its business write. Payloads carry
  //   non-PII metadata/field deltas only — never client contact data.
  //   `quickSet` is the one-click "mark fully paid" shortcut, distinct from
  //   the regular `payment.set`.
  'cargo.create',
  'cargo.update',
  'cargo.delete',
  'cargo.import',
  'cargo.document.add',
  'cargo.document.delete',
  'cargo.payment.set',
  'cargo.payment.quickSet',
  'cargo.clientTariff.set',
  'cargo.chinaPayment.set',
  'cargo.carrierPayment.set',
  'cargo.dictionary.change',
  // INVARIANT (#1261): CRM lead-intake admin codes. `AuditEvent.action`
  //   is a plain `String` column (see `audit.prisma`), so these are
  //   shared-only catalog additions — NO Prisma enum and NO migration.
  //   `leadIntakeDuty.toggle` is emitted by `PATCH /lead-intake-duty/:userId`
  //   when a manager's `LeadIntakeManagerDuty.onDuty` flips (no-op toggles
  //   emit nothing); it is INTENTIONALLY distinct from `user.shift-toggle`,
  //   which audits the unrelated logistics `User.isOnShift` column.
  //   `callCenterAgent.bind` is emitted by both the create
  //   (`POST /call-center-agents`) and rebind
  //   (`PATCH /call-center-agents/:userId/binding`) paths; the diff's
  //   `sourceId.before`/`after` distinguishes initial bind (no before)
  //   from a rebind. Payloads carry ids/typed deltas only — never
  //   phone/name/comment or the source secret.
  'leadIntakeDuty.toggle',
  'callCenterAgent.bind',
  // INVARIANT (#1936): Cargo client-payment review-workflow audit codes,
  //   appended after the existing `cargo.payment.*` entries. Shared-only
  //   catalog additions (no Prisma enum / migration). `cargo.payment.requestStatus`
  //   is emitted when the responsible Manager submits a desired client-payment
  //   status for review (`PATCH /cargo/:id/client-payment-status`), which sets
  //   `requestedClientPaymentStatus` + flips `paidByClientStatus` to
  //   `under_review`. `cargo.payment.review` is emitted by the financier review
  //   batch (`PATCH /cargo/:id/client-payment-review`) once the per-document
  //   decisions commit. Both diffs carry the status transition + ids only —
  //   never fileName / objectKey / PII.
  'cargo.payment.requestStatus',
  'cargo.payment.review',
  // INVARIANT (#2783): Cargo Problems audit payloads carry ids and typed
  // deltas only — never descriptions, comment bodies, file names/object keys,
  // or Cargo-derived fields. Timeline content stays in its owning tables.
  'cargoProblem.create',
  'cargoProblem.update',
  'cargoProblem.transition',
  'cargoProblem.delete',
  'cargoProblem.comment',
  'cargoProblem.file.upload',
  // INVARIANT (#2863): Cargo Problems workspace codes. `cargoProblem.comment`
  // keeps covering a posted message (a message IS a comment row) and
  // `cargoProblem.file.upload` its attachments; these three cover what the
  // workspace adds. `cargoProblem.reaction.escalate` is written by the SLA
  // scheduler, which has no principal — the global audit row accepts a null
  // actor, which is why reaction escalation is durable WITHOUT a
  // `CargoProblemEvent` (that table's `actorId` is required and FK-anchored).
  // Diffs stay ids and typed deltas: never a message body, a file name, an
  // object key, or the participant-visible deletion reason.
  'cargoProblem.assignee.change',
  'cargoProblem.attachment.delete',
  'cargoProblem.reaction.escalate',
] as const;
